import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { TermoCompromissoDirecaoPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/termo-compromisso-direcao-pj/termo-compromisso-direcao-pj.service';
import { ReportTermoCompromissoDirecaoPjRequest, ReportTermoCompromissoDirecaoPjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/termo-compromisso-direcao-pj/termo-compromisso-direcao-pj.model';
import { TermoCompromissoDirecaoPjJobService } from '@/core/services/termo-compromisso-direcao-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportTermoCompromissoDirecaoPjRequest;
type Resp = ReportTermoCompromissoDirecaoPjResponse;

@Component({
    selector: 'app-termo-compromisso-direcao-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, RelatorioContainer, CanDirective],
    templateUrl: './termo-compromisso-direcao-pj.html',
    styleUrl: './termo-compromisso-direcao-pj.scss'
})
/**
 * Relatório PDF assíncrono: Termo de Compromisso de Direção do prestador (PJ).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (nuCrm/sgUf via `PessoaService`). Sem filtros adicionais.
 */
export class ReportTermoCompromissoDirecaoPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly relatorioService = inject(TermoCompromissoDirecaoPjService);
    private readonly pessoaService = inject(PessoaService);
    readonly jobService = inject(TermoCompromissoDirecaoPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.jobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.jobService;
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar o termo.'
            });
            return null;
        }

        return { nuCrm, sgUf };
    }

    protected exportar(request: Req) {
        return this.relatorioService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.limparJob();
    }
}
